import { useEffect, useRef, useState } from "react";
import { Plus, Upload, Trash2, Pencil, Eye, EyeOff, Users, CheckCircle2, ExternalLink } from "lucide-react";
import { staffApi, apiError } from "../lib/api";

const KINDS = [
  { key: "task", label: "Tasks" },
  { key: "repertoire", label: "Repertoire" },
  { key: "resource", label: "Resources" },
  { key: "lesson", label: "Lessons" },
];
const KIND_LABEL = Object.fromEntries([["task", "Task"], ["repertoire", "Repertoire"], ["resource", "Resource"], ["lesson", "Lesson"]]);

const EMPTY = { kind: "task", title: "", body: "", external_url: "", composer: "", due_date: "", assign_type: "institution", group_ids: [], published: true };

export default function ContentPage() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tab, setTab] = useState("all");
  const [form, setForm] = useState(null);   // null = closed; {} = create; {id} = edit
  const [error, setError] = useState("");
  const [uploadFor, setUploadFor] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [completions, setCompletions] = useState({}); // contentId -> list
  const [fbDraft, setFbDraft] = useState({});         // "contentId:choristerId" -> note text
  const fileRef = useRef(null);

  const load = () => staffApi.get("/staff/content").then((r) => setItems(r.data.items)).catch((e) => setError(apiError(e)));
  useEffect(() => {
    load();
    staffApi.get("/staff/groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, []);

  const save = async () => {
    setError("");
    const payload = {
      kind: form.kind, title: form.title, body: form.body,
      external_url: form.external_url || null,
      composer: form.composer || null,
      due_date: form.due_date || null,
      assign_type: form.assign_type,
      group_ids: form.assign_type === "group" ? form.group_ids : [],
      published: form.published,
    };
    try {
      if (form.id) {
        const { id, kind, ...patch } = { ...payload, id: form.id };
        await staffApi.put(`/staff/content/${form.id}`, patch);
      } else {
        await staffApi.post("/staff/content", payload);
      }
      setForm(null);
      load();
    } catch (e) { setError(apiError(e)); }
  };

  const togglePublish = async (it) => {
    await staffApi.put(`/staff/content/${it.id}`, { published: !it.published }).catch((e) => setError(apiError(e)));
    load();
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"? This cannot be undone.`)) return;
    await staffApi.delete(`/staff/content/${it.id}`).catch((e) => setError(apiError(e)));
    load();
  };

  const upload = async (file) => {
    if (!file || !uploadFor) return;
    setUploadPct(1);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await staffApi.post(`/staff/content/${uploadFor}/file`, fd, {
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded / Math.max(1, e.total)) * 100)),
      });
      setUploadFor(null);
      setUploadPct(0);
      load();
    } catch (e) { setError(apiError(e)); setUploadFor(null); setUploadPct(0); }
  };

  const showCompletions = async (it) => {
    if (completions[it.id]) { setCompletions((c) => ({ ...c, [it.id]: null })); return; }
    const r = await staffApi.get(`/staff/content/${it.id}/completions`).catch(() => null);
    setCompletions((c) => ({ ...c, [it.id]: r?.data?.completions || [] }));
  };

  const sendFeedback = async (it, choristerId) => {
    const key = `${it.id}:${choristerId}`;
    const note = (fbDraft[key] || "").trim();
    if (!note) return;
    try {
      await staffApi.post(`/staff/content/${it.id}/feedback`, { chorister_id: choristerId, note });
      setFbDraft((d) => ({ ...d, [key]: "" }));
      const r = await staffApi.get(`/staff/content/${it.id}/completions`).catch(() => null);
      if (r) setCompletions((c) => ({ ...c, [it.id]: r.data.completions || [] }));
    } catch (e) { setError(apiError(e)); }
  };

  const shown = items.filter((i) => tab === "all" || i.kind === tab);

  return (
    <div data-testid="content-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B192C]">Our Content</h1>
          <p className="text-sm text-[#566270] mt-1">Your choir's private material — only your choristers can see it.</p>
        </div>
        <button onClick={() => setForm({ ...EMPTY })} data-testid="content-new-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#7A1C2E] text-white font-bold text-sm hover:bg-[#8F2438] min-h-[48px]">
          <Plus className="w-4 h-4" aria-hidden /> New item
        </button>
      </div>

      {error && <p className="mt-4 text-sm text-[#A62B2B] font-semibold" data-testid="content-error">{error}</p>}

      <div className="flex gap-2 mt-5 flex-wrap" data-testid="content-tabs">
        {[{ key: "all", label: "All" }, ...KINDS].map((k) => (
          <button key={k.key} onClick={() => setTab(k.key)} data-testid={`content-tab-${k.key}`}
            className={`px-4 py-2.5 rounded-xl text-sm font-bold min-h-[44px] ${tab === k.key ? "bg-[#0B192C] text-[#FAF7EE]" : "bg-white text-[#566270] border border-[#E5DEC9]"}`}>
            {k.label}
          </button>
        ))}
      </div>

      {form && (
        <div className="mt-5 rounded-2xl bg-white border-2 border-[#C69B3D]/40 p-5 space-y-3" data-testid="content-form">
          <h2 className="font-display font-bold text-[#0B192C]">{form.id ? "Edit item" : "New item"}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm font-semibold text-[#1C242B]">Type
              <select value={form.kind} disabled={!!form.id} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="content-form-kind"
                className="mt-1 w-full rounded-xl border border-[#E5DEC9] px-3 py-2.5 min-h-[44px]">
                {KINDS.map((k) => <option key={k.key} value={k.key}>{KIND_LABEL[k.key]}</option>)}
              </select>
            </label>
            <label className="text-sm font-semibold text-[#1C242B]">Title
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="content-form-title"
                className="mt-1 w-full rounded-xl border border-[#E5DEC9] px-3 py-2.5 min-h-[44px]" />
            </label>
          </div>
          <label className="block text-sm font-semibold text-[#1C242B]">Details / instructions
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} data-testid="content-form-body"
              className="mt-1 w-full rounded-xl border border-[#E5DEC9] px-3 py-2.5" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            {form.kind === "repertoire" && (
              <label className="text-sm font-semibold text-[#1C242B]">Composer
                <input value={form.composer} onChange={(e) => setForm({ ...form, composer: e.target.value })} data-testid="content-form-composer"
                  className="mt-1 w-full rounded-xl border border-[#E5DEC9] px-3 py-2.5 min-h-[44px]" />
              </label>
            )}
            {(form.kind === "resource" || form.kind === "repertoire" || form.kind === "lesson") && (
              <label className="text-sm font-semibold text-[#1C242B]">External link (optional)
                <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" data-testid="content-form-url"
                  className="mt-1 w-full rounded-xl border border-[#E5DEC9] px-3 py-2.5 min-h-[44px]" />
              </label>
            )}
            {form.kind === "task" && (
              <>
                <label className="text-sm font-semibold text-[#1C242B]">Due date
                  <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="content-form-due"
                    className="mt-1 w-full rounded-xl border border-[#E5DEC9] px-3 py-2.5 min-h-[44px]" />
                </label>
                <label className="text-sm font-semibold text-[#1C242B]">Assign to
                  <select value={form.assign_type} onChange={(e) => setForm({ ...form, assign_type: e.target.value })} data-testid="content-form-assign"
                    className="mt-1 w-full rounded-xl border border-[#E5DEC9] px-3 py-2.5 min-h-[44px]">
                    <option value="institution">Whole choir</option>
                    <option value="group">Specific groups…</option>
                  </select>
                </label>
              </>
            )}
          </div>
          {form.kind === "task" && form.assign_type === "group" && (
            <div className="flex gap-2 flex-wrap" data-testid="content-form-groups">
              {groups.map((g) => (
                <label key={g.id} className={`px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer min-h-[44px] flex items-center ${form.group_ids.includes(g.id) ? "bg-[#0B192C] text-[#FAF7EE]" : "bg-[#F3EFE0] text-[#566270]"}`}>
                  <input type="checkbox" className="mr-2" checked={form.group_ids.includes(g.id)}
                    data-testid={`content-form-group-${g.id}`}
                    onChange={(e) => setForm({ ...form, group_ids: e.target.checked ? [...form.group_ids, g.id] : form.group_ids.filter((x) => x !== g.id) })} />
                  {g.name}
                </label>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm font-semibold text-[#1C242B] min-h-[44px]">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="content-form-published" />
            Published (visible to choristers)
          </label>
          <div className="flex gap-2">
            <button onClick={save} data-testid="content-form-save"
              className="px-5 py-3 rounded-xl bg-[#2D6A4F] text-white font-bold text-sm min-h-[48px]">{form.id ? "Save changes" : "Create"}</button>
            <button onClick={() => setForm(null)} data-testid="content-form-cancel"
              className="px-5 py-3 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[48px]">Cancel</button>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" className="hidden" accept="audio/*,image/*,application/pdf,text/plain"
        data-testid="content-file-input" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />

      <div className="mt-5 space-y-3">
        {shown.length === 0 && <p className="text-sm text-[#566270]" data-testid="content-empty">Nothing here yet — create your choir's first item.</p>}
        {shown.map((it) => (
          <div key={it.id} className="rounded-2xl bg-white border border-[#E5DEC9] p-4 sm:p-5" data-testid={`content-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#F3EFE0] text-[#566270]">{KIND_LABEL[it.kind]}</span>
                  {!it.published && <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#F6EED8] text-[#B8860B]">Draft</span>}
                  {it.due_date && <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#7A1C2E]/10 text-[#7A1C2E]">Due {it.due_date}</span>}
                  {it.kind === "task" && <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F]">{it.completed_count || 0} done</span>}
                </div>
                <h3 className="font-display font-bold text-[#0B192C] mt-1.5">{it.title}{it.composer ? <span className="font-normal text-[#566270]"> — {it.composer}</span> : null}</h3>
                {it.body && <p className="text-sm text-[#566270] mt-1 whitespace-pre-wrap">{it.body}</p>}
                {it.external_url && (
                  <a href={it.external_url} target="_blank" rel="noreferrer" data-testid={`content-link-${it.id}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#7A1C2E] mt-1 min-h-[44px]">
                    <ExternalLink className="w-3.5 h-3.5" aria-hidden /> {it.external_url}
                  </a>
                )}
                {it.file_id && <p className="text-xs text-[#2D6A4F] font-semibold mt-1">File attached</p>}
                {completions[it.id] && (
                  <ul className="mt-2 space-y-2" data-testid={`content-completions-${it.id}`}>
                    {completions[it.id].length === 0 && <li className="text-sm text-[#566270]">No completions yet.</li>}
                    {completions[it.id].map((c) => (
                      <li key={c.chorister_id} className="text-sm text-[#1C242B]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" aria-hidden /> {c.display_name}
                          <span className="text-xs text-[#566270]">{new Date(c.done_at).toLocaleDateString("en-GB")}</span>
                        </div>
                        {(c.feedback || []).map((f, i) => (
                          <p key={i} className="ml-6 mt-1 text-xs bg-[#F3EFE0] rounded-xl px-3 py-2 text-[#1C242B] inline-block">
                            <span className="font-bold">{f.staff_name}:</span> {f.note}
                          </p>
                        ))}
                        <div className="flex gap-2 mt-1 ml-6">
                          <input value={fbDraft[`${it.id}:${c.chorister_id}`] || ""}
                            onChange={(e) => setFbDraft((d) => ({ ...d, [`${it.id}:${c.chorister_id}`]: e.target.value }))}
                            placeholder="Encourage or advise…" data-testid={`feedback-input-${it.id}-${c.chorister_id}`}
                            className="flex-1 min-w-[160px] rounded-xl border border-[#E5DEC9] px-3 py-2 text-sm min-h-[40px]" />
                          <button onClick={() => sendFeedback(it, c.chorister_id)} data-testid={`feedback-send-${it.id}-${c.chorister_id}`}
                            className="px-3 py-2 rounded-xl bg-[#0B192C] text-[#FAF7EE] text-xs font-bold min-h-[40px]">Send</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={() => { setUploadFor(it.id); fileRef.current?.click(); }} data-testid={`content-upload-${it.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#0B192C] text-[#FAF7EE] text-xs font-bold min-h-[44px]">
                  <Upload className="w-3.5 h-3.5" aria-hidden /> {uploadFor === it.id && uploadPct ? `${uploadPct}%` : "File"}
                </button>
                {it.kind === "task" && (
                  <button onClick={() => showCompletions(it)} data-testid={`content-completions-btn-${it.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-[#E5DEC9] text-[#566270] text-xs font-bold min-h-[44px]">
                    <Users className="w-3.5 h-3.5" aria-hidden /> Who's done
                  </button>
                )}
                <button onClick={() => setForm({ id: it.id, kind: it.kind, title: it.title, body: it.body || "", external_url: it.external_url || "", composer: it.composer || "", due_date: it.due_date || "", assign_type: it.assign_type || "institution", group_ids: it.group_ids || [], published: it.published })}
                  data-testid={`content-edit-${it.id}`} aria-label={`Edit ${it.title}`}
                  className="p-2.5 rounded-xl border border-[#E5DEC9] text-[#566270] min-h-[44px] min-w-[44px] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => togglePublish(it)} data-testid={`content-publish-${it.id}`} aria-label={it.published ? "Unpublish" : "Publish"}
                  className="p-2.5 rounded-xl border border-[#E5DEC9] text-[#566270] min-h-[44px] min-w-[44px] flex items-center justify-center">
                  {it.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => remove(it)} data-testid={`content-delete-${it.id}`} aria-label={`Delete ${it.title}`}
                  className="p-2.5 rounded-xl border border-[#A62B2B]/30 text-[#A62B2B] min-h-[44px] min-w-[44px] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
